// "Open this article on the member site" icon link.
//
// Renders nothing when there is no URL to build (no slug, or a publication
// with no member site) rather than showing a dead control.

import { memberArticleUrl, memberIssueUrl } from './memberLink'
import { usePublicBaseUrl } from '../lib/publicBaseUrls'

/** The glyph on its own, for callers that are already inside an anchor —
 *  nesting <a> inside <a> is invalid HTML. */
export function ExternalIcon() {
  return (
    <svg
      width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

export function MemberPageLink({
  publicationSlug, slug, status, className = '',
}: {
  publicationSlug: string
  slug: string | null | undefined
  status?: string
  className?: string
}) {
  const base = usePublicBaseUrl(publicationSlug)
  const url = memberArticleUrl(base, slug)
  if (!url) return null

  // An unpublished article is hidden from members. Staff signed in as a
  // member (staff view) see it as a preview; anyone else gets "not found".
  const live = !status || status === 'active'
  const title = live
    ? 'Open on the member site'
    : 'Preview on the member site — opens only in a staff view (sign in as a member first)'

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center ${
        live ? 'text-paper-trace hover:text-amber-glow' : 'text-paper-trace/50 hover:text-paper-trace'
      } ${className}`}
    >
      <ExternalIcon />
    </a>
  )
}

/** The same link for a digital-edition issue, whose member page is keyed
 *  by edition id rather than a slug. */
export function MemberIssueLink({
  publicationSlug, viewerUrl, status, rendered = true, className = '',
}: {
  publicationSlug: string
  viewerUrl: string | null | undefined
  status?: string
  /** False when no pages have been rendered — the page resolves but shows
   *  the site's "coming soon" placeholder. */
  rendered?: boolean
  className?: string
}) {
  const base = usePublicBaseUrl(publicationSlug)
  const url = memberIssueUrl(base, viewerUrl)
  if (!url) return null

  // The member endpoint scopes its lookup to status='active', so a draft
  // does not merely hide its contents — the URL 404s. Worth saying, since
  // the icon otherwise looks broken.
  const live = !status || status === 'active'
  const title = !live
    ? 'Open on the member site — not published, so this URL will 404'
    : rendered
      ? 'Open on the member site'
      : 'Open on the member site — no pages rendered yet, so it shows "coming soon"'

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center ${
        live && rendered
          ? 'text-paper-trace hover:text-amber-glow'
          : 'text-paper-trace/50 hover:text-paper-trace'
      } ${className}`}
    >
      <ExternalIcon />
    </a>
  )
}
